import type { DisplayState, ScreenId } from '../protocol/types';

/**
 * Ultimo stato ricevuto, salvato nel dispositivo (IndexedDB): se l'app si riapre senza rete
 * mostra questi dati, che per i loro `updatedAt` risultano "non aggiornati".
 * Ogni errore (finestra privata, dati bloccati, spazio pieno) è ignorato: la cache è un di più.
 */
const DB = 'tiw-display';
const STORE = 'state';
export const MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface Saved {
  savedAt: number;
  data: Partial<DisplayState>;
}

const keyOf = (screen: ScreenId) => `state:${screen}`;

async function run<T>(
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  try {
    const idb = globalThis.indexedDB;
    if (!idb) return null;
    const db = await new Promise<IDBDatabase>((resolve, reject) => {
      const open = idb.open(DB, 1);
      open.onupgradeneeded = () => open.result.createObjectStore(STORE);
      open.onsuccess = () => resolve(open.result);
      open.onerror = () => reject(open.error);
      open.onblocked = () => reject(new Error('database bloccato'));
    });
    try {
      return await new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const req = fn(tx.objectStore(STORE));
        tx.oncomplete = () => resolve(req.result);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      });
    } finally {
      db.close();
    }
  } catch {
    return null;
  }
}

export async function saveState(
  screen: ScreenId,
  data: Partial<DisplayState>,
  savedAt: number = Date.now(),
): Promise<void> {
  const value: Saved = { savedAt, data };
  await run('readwrite', (s) => s.put(value, keyOf(screen)));
}

export async function clearState(screen: ScreenId): Promise<void> {
  await run('readwrite', (s) => s.delete(keyOf(screen)));
}

const isSaved = (v: unknown): v is Saved =>
  !!v &&
  typeof v === 'object' &&
  typeof (v as Saved).savedAt === 'number' &&
  !!(v as Saved).data &&
  typeof (v as Saved).data === 'object';

/** Lo stato salvato, oppure null se manca, è rovinato o ha più di 24 ore (in quel caso viene cancellato). */
export async function loadState(screen: ScreenId, now: number = Date.now()): Promise<Saved | null> {
  const v = await run('readonly', (s) => s.get(keyOf(screen)));
  if (!isSaved(v) || now - v.savedAt > MAX_AGE_MS) {
    if (v != null) await clearState(screen);
    return null;
  }
  return { savedAt: v.savedAt, data: v.data };
}
